<template>
  <div class="ma-4 px-4">
    <searchBarFilter
      @add="(dialog = true), (editMode = false)"
      @updateSearch="(v) => (search = v)"
      @error="errorDownload"
      @uploaded="initialise"
      @help="showHelp = true"
      :uploadButton="true"
      :downloadButton="true"
      headerText="Course"
      class="mt-2"
      :uploadUrl="'upload_courses'"
      :downloadUrl="'download_courses'"
    />

    <v-row>
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="headers"
            :search="search"
            :items="courses"
            fixed-header
            class="secondary row-height-50"
          >
            <template #[`item.name`]="{ item }">
              <span
                @click="showCourseUnit(item)"
                style="
                  cursor: pointer;
                  color: #4f4584;
                  text-decoration: underline;
                "
                >{{ item.name }}</span
              >
            </template>
            <template #[`item.action`]="{ item }">
              <v-icon small class="mr-2" color="accent" @click="edit(item)">
                mdi-pencil
              </v-icon>
            </template>
            <template #[`item.active`]="{ item }">
              <v-chip
                small
                :color="item.active ? 'green' : 'red'"
                dark
                @click="openConfirm(item)"
              >
                {{ item.active_status }}
              </v-chip>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>
    <v-dialog v-model="dialog" width="800" persistent scrollable>
      <formDialog
        v-if="dialog"
        @closeDialog="dialog = false"
        @create="createCourse"
        @update="updateCourse"
        :course="course"
        :departments="departments"
        :edit="editMode"
      />
    </v-dialog>
    <ConfirmDialog
      v-if="showDialog"
      :show="showDialog"
      @delete="changeConfirmation"
      @close="showDialog = false"
      title="course"
      :name="currentItem.name"
      :active="currentItem.active"
    />

    <v-dialog v-model="showDataDialog" max-width="600" persistent scrollable>
      <v-card class="secondary">
        <v-card-title class="primary white--text"
          >{{ selectedItem.name }} - {{ selectedItem.content_name }}
          <v-spacer></v-spacer>
          <v-btn small icon @click="showDataDialog = false">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn></v-card-title
        >
        <v-divider></v-divider>
        <v-card-text class="mt-3 reset-card-text">
          <div v-html="selectedItem.description"></div>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="uploadErrorDialog" width="450" persistent>
      <uploadError
        v-if="uploadErrorDialog"
        :errorCsvBlob="errorCsvBlob"
        @downloaded="
          initialise, (uploadErrorDialog = false), (errorCsvBlob = null)
        "
        csvTitle="Course"
      />
    </v-dialog>
    <v-dialog v-model="showHelp" max-width="700px" persistent>
      <v-card>
        <v-card-title class="font-weight-medium secondary--text primary">
          CSV Upload Instructions
          <v-spacer></v-spacer>
          <v-btn small icon @click="showHelp = false">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <h4>📌 General Rules</h4>
          <ul>
            <li>Do not change the column names.</li>
          </ul>
          <h4>📘 Sub Type</h4>
          <ul>
            <li>This field is <strong>case-sensitive</strong>.</li>
            <li>Only the following values are allowed:</li>
          </ul>
          <ul>
            Example:
            <strong>Theory</strong
            >,
            <strong>Lab</strong
            >,
            <strong>ST&SP</strong
            >,
            <strong>ST&SL</strong
            >,
            <strong>Project</strong
            >.
          </ul>
          <h4>📘 Department</h4>
          <ul>
            <li>Enter the <strong>Department Code</strong> only</li>
            <li>Example: <strong>SOC</strong>, <strong>SOM</strong>, etc.</li>
          </ul>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import formDialog from "@/components/course/form.vue";
import searchBarFilter from "@/components/searchBarFilter.vue";
import { courseHeaders } from "@/constants/headers";
import ConfirmDialog from "@/components/confirm.vue";
import uploadError from "@/components/uploadError.vue";

export default {
  components: {
    formDialog,
    searchBarFilter,
    ConfirmDialog,
    uploadError,
  },
  data() {
    return {
      dialog: false,
      editMode: false,
      department: {},
      showDialog: false,
      currentItem: {},
      courses: [],
      departments: [],
      search: "",
      headers: courseHeaders,
      selectedItem: {},
      showDataDialog: false,
      uploadErrorDialog: false,
      errorCsvBlob: null,
      showHelp: false,
    };
  },
  watch: {},
  computed: {},
  created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Courses" }]);
    this.initialise();
  },
  methods: {
    initialise() {
      this.getCourses();
      this.getDepartments();
    },
    async getCourses() {
      await this.$axios
        .get("courses")
        .then((res) => {
          const course_data = res.data.data;
          this.courses = course_data.sort((a, b) => b.id - a.id);
        })
        .catch(async (error) => {
          this.$store.commit("showSnackbar", {
            message: error.response.data.message,
            color: "error",
          });
        });
    },
    async getDepartments() {
      await this.$axios
        .get("departments")
        .then((res) => {
          this.departments = res.data.data;
        })
        .catch(async (error) => {
          this.$store.commit("showSnackbar", {
            message: error.response.data.message,
            color: "error",
          });
        });
    },
    async createCourse(data) {
      await this.$axios
        .post("courses", { course: data })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Course created successfully",
            color: "success",
          });
          this.resetValues();
          this.initialise();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response,
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
    async updateCourse(data) {
      await this.$axios
        .put("courses/" + data.id, { course: data })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Course updated successfully",
            color: "success",
          });
          this.resetValues();
          this.initialise();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response,
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
    changeConfirmation() {
      this.updateCourse({
        id: this.currentItem.id,
        active: !this.currentItem.active,
      });
    },
    openConfirm(item) {
      this.showDialog = true;
      this.currentItem = { ...item };
    },
    edit(item) {
      this.course = { ...item };
      this.editMode = true;
      this.dialog = true;
    },
    resetValues() {
      this.editMode = false;
      this.dialog = false;
      this.showDialog = false;
      this.currentItem = {};
      this.course = {};
    },
    showCourseUnit(item) {
      this.$router.push({
        path: `/course_sections`,
        query: {
          id: item.id,
          name: item.name,
        },
      });
    },
    showData(name, content, content_name) {
      this.showDataDialog = true;
      this.selectedItem.description = content;
      this.selectedItem.name = name;
      this.selectedItem.content_name = content_name;
    },
    errorDownload(data) {
      this.$store.commit("showSnackbar", {
        message: "Upload Failed",
        color: "error",
      });
      this.dialog = false;
      this.errorCsvBlob = new Blob([data]);
      this.uploadErrorDialog = true;
    },
  },
};
</script>

<style>
.reset-card-text {
  font-size: medium !important;
  font-weight: normal !important;
  line-height: normal !important;
  letter-spacing: normal !important;
}
</style>
